import type { ParsedCoordinates } from './types';

interface Group {
  hemi: string | null;
  nums: string[];
}

const NUMBER = /[-+]?\d+(?:\.\d+)?/g;
const HEMI = /[NSEW]/;

/** Splits tokens into a lat and a lng group by hemisphere letters, or by separators/count without them. */
function groupTokens(s: string): Group[] | null {
  const tokens: string[] = s.match(/[NSEW]|[-+]?\d+(?:\.\d+)?/g) ?? [];
  const letters = tokens.filter((t) => HEMI.test(t));
  const groups: Group[] = [];

  if (letters.length === 2) {
    if (HEMI.test(tokens[0])) {
      // "N 40 46 52 W 111 49 45"
      for (const t of tokens) {
        if (HEMI.test(t)) groups.push({ hemi: t, nums: [] });
        else groups[groups.length - 1].nums.push(t);
      }
    } else {
      // "40 46 52 N 111 49 45 W"
      let nums: string[] = [];
      for (const t of tokens) {
        if (HEMI.test(t)) {
          groups.push({ hemi: t, nums });
          nums = [];
        } else nums.push(t);
      }
      if (nums.length) return null;
    }
    return groups;
  }

  if (letters.length === 0) {
    const parts = s.split(/[,;]/).filter((p) => p.trim());
    if (parts.length === 2) return parts.map((p) => ({ hemi: null, nums: p.match(NUMBER) || [] }));
    if (parts.length === 1 && [2, 4, 6].includes(tokens.length)) {
      const half = tokens.length / 2;
      return [
        { hemi: null, nums: tokens.slice(0, half) },
        { hemi: null, nums: tokens.slice(half) },
      ];
    }
  }
  return null;
}

/** Degrees (decimal, DMS or DDM) with optional N/S/E/W before or after each part. */
export function parseDegrees(text: string): ParsedCoordinates | null {
  const s = text
    .toUpperCase()
    .replace(/[‘’′]/g, "'")
    .replace(/[“”″]/g, '"')
    .replace(/([-+])\s+(?=\d)/g, '$1') // "- 106.3" is meant as "-106.3"
    .trim();
  if (/[^NSEW\d\s.,;:+\-°º'"]/.test(s)) return null;
  const groups = groupTokens(s);
  if (!groups || groups.length !== 2) return null;

  const values = [];
  for (const { hemi, nums } of groups) {
    if (nums.length < 1 || nums.length > 3) return null;
    const [d, m = 0, sec = 0] = nums.map(Number);
    if (m < 0 || m >= 60 || sec < 0 || sec >= 60) return null;
    if (nums.length > 1 && !Number.isInteger(d)) return null;
    const neg = nums[0].startsWith('-') || hemi === 'S' || hemi === 'W';
    values.push({ hemi, v: (neg ? -1 : 1) * (Math.abs(d) + m / 60 + sec / 3600), dms: nums.length > 1 });
  }
  let [a, b] = values;
  if (a.hemi && b.hemi) {
    const aLat = 'NS'.includes(a.hemi);
    const bLat = 'NS'.includes(b.hemi);
    if (aLat === bLat) return null;
    if (!aLat) [a, b] = [b, a];
  } else if (Math.abs(a.v) > 90 && Math.abs(b.v) <= 90) [a, b] = [b, a]; // "lng, lat"
  if (Math.abs(a.v) > 90 || Math.abs(b.v) > 180) return null;
  return { lat: a.v, lng: b.v, format: a.dms || b.dms ? 'dms' : 'decimal' };
}
